/**
 * Headline tiles for the Availability report, and the row filter each one
 * applies when clicked.
 *
 * Each clicked tile shows every row the tile mentions.
 *
 * - **Bookable free hours**: Σ positive net over Some Room / Available cells.
 * - **Over-utilized**: people with at least one Over-utilized (≥ over
 *   threshold) cell in the window — Stretched does NOT count, because
 *   Over-utilized is a named state — plus Σ hours over on those Over-utilized
 *   cells. Its "N chronic" sub-line counts Chronic rows among ALL rows: a
 *   Chronic person at Stretched this window still matters. Clicking the tile
 *   shows people OR Chronic, so everyone the tile mentions is in the grid.
 * - **Rolling off**: people flagged Rolling Off.
 * - **Data gaps**: people with no Home Role, people with an open issue, and
 *   window months past the plan horizon.
 *
 * 🔒 FIREWALL: HOURS only. No Fit Score, Capacity, Intensity, or points imports.
 */
import { type CellResult, isFreeState } from "@/lib/availability/cell-state";

export type TileFilter =
  | "bookableFree"
  | "overUtilized"
  | "chronic"
  | "rollingOff"
  | "noHomeRole"
  | "issues"
  | "pastHorizon";

/** A row whose cells have already been classified, plus its row-level flags. */
export interface ClassifiedRow {
  resourceId: string;
  homeRole: string | null;
  chronic: boolean;
  rollingOff: boolean;
  hasIssue: boolean;
  /** Keyed by `"YYYY-MM-01"`. */
  cells: Record<string, CellResult>;
}

export interface AvailabilityTiles {
  bookableFreeHours: number;
  overUtilized: { people: number; chronic: number; hoursOver: number };
  rollingOff: number;
  dataGaps: { noHomeRole: number; issues: number; pastHorizonMonths: number };
}

/** Round to one decimal place, so a stored value and a displayed one agree. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function windowCells(row: ClassifiedRow, windowMonths: readonly string[]): CellResult[] {
  const cells: CellResult[] = [];
  for (const month of windowMonths) {
    const cell = row.cells[month];
    if (cell !== undefined) cells.push(cell);
  }
  return cells;
}

function isBookable(cell: CellResult): boolean {
  return isFreeState(cell.state) && cell.netHours > 0;
}

function isOverUtilized(cell: CellResult): boolean {
  return cell.state === "overUtilized";
}

function hasOverUtilizedCell(row: ClassifiedRow, windowMonths: readonly string[]): boolean {
  return windowCells(row, windowMonths).some(isOverUtilized);
}

/** The row predicate a tile applies to the grid. */
export function tileFilter(
  tile: TileFilter,
  windowMonths: readonly string[],
): (row: ClassifiedRow) => boolean {
  switch (tile) {
    case "bookableFree":
      return (row) => windowCells(row, windowMonths).some(isBookable);
    case "overUtilized":
      return (row) => hasOverUtilizedCell(row, windowMonths);
    case "chronic":
      return (row) => row.chronic;
    case "rollingOff":
      return (row) => row.rollingOff;
    case "noHomeRole":
      return (row) => row.homeRole === null || row.homeRole.trim() === "";
    case "issues":
      return (row) => row.hasIssue;
    case "pastHorizon":
      return (row) => windowCells(row, windowMonths).some((c) => c.state === "unplanned");
  }
}

/**
 * `planHorizon` is the last planned month (see `planHorizon` in signals), or
 * null when no month is planned — then every window month is past it.
 */
export function computeTiles(
  rows: readonly ClassifiedRow[],
  windowMonths: readonly string[],
  planHorizon: string | null,
): AvailabilityTiles {
  let bookableFreeHours = 0;
  let hoursOver = 0;
  for (const row of rows) {
    for (const cell of windowCells(row, windowMonths)) {
      if (isBookable(cell)) bookableFreeHours += cell.netHours;
      if (isOverUtilized(cell)) hoursOver += -cell.netHours;
    }
  }

  const count = (tile: TileFilter) => rows.filter(tileFilter(tile, windowMonths)).length;

  return {
    bookableFreeHours: round1(bookableFreeHours),
    overUtilized: {
      people: count("overUtilized"),
      // Chronic among the people the tile counts, so clicking it shows exactly them.
      chronic: rows.filter((row) => row.chronic && hasOverUtilizedCell(row, windowMonths)).length,
      hoursOver: round1(hoursOver),
    },
    rollingOff: count("rollingOff"),
    dataGaps: {
      noHomeRole: count("noHomeRole"),
      issues: count("issues"),
      pastHorizonMonths: windowMonths.filter((m) => planHorizon === null || m > planHorizon).length,
    },
  };
}
